import React, { useState, useEffect } from 'react';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import FeatureGate from './FeatureGate';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import crmApi from '../../utils/crmApi';
import { 
  HeartIcon,
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const EngagementHealth = () => {
  const { user, currentAgency } = useCRM();
  const { branding } = useBranding();
  const { hasFeature } = useSubscription();
  
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);

  useEffect(() => {
    if (currentAgency?._id && hasFeature('advancedAnalytics')) {
      loadHealthData();
    }
  }, [currentAgency?._id, hasFeature]);

  const loadHealthData = async () => {
    if (!currentAgency?._id) return;
    
    setLoading(true);
    try {
      const response = await crmApi.get('/engagement-health/overview');
      const data = response.data;
      if (data.success) {
        setHealthData(data.data);
      } else {
        console.error('Failed to load engagement health:', data.message);
      }
    } catch (error) {
      console.error('Failed to load engagement health:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FeatureGate feature="advancedAnalytics" requiredTier="Professional">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center">
              <HeartIcon className="h-7 w-7 mr-2" style={{ color: branding.primaryColor }} />
              Engagement Health
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor and improve client engagement across your agency
            </p>
          </div>
        </div>

        {/* Health Overview */}
        {healthData && !loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-muted-foreground">Overall Health</div>
                <HeartIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-foreground">{healthData.overallHealth}%</div>
              <div className="mt-2 text-xs text-muted-foreground">120 Active Clients</div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-muted-foreground">Excellent</div>
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-green-600">{healthData.excellent}</div>
              <div className="mt-2 text-xs text-muted-foreground">Clients</div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-muted-foreground">Good</div>
                <ClockIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-blue-600">{healthData.good}</div>
              <div className="mt-2 text-xs text-muted-foreground">Clients</div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-muted-foreground">Needs Attention</div>
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="text-3xl font-bold text-yellow-600">{healthData.needsAttention}</div>
              <div className="mt-2 text-xs text-muted-foreground">Clients</div>
            </Card>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <Card className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading engagement health data...</p>
          </Card>
        )}

        {/* Coming Soon */}
        {!loading && !healthData && (
          <Card className="p-12 text-center">
            <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Engagement Health Analytics</h3>
            <p className="text-muted-foreground mb-6">
              Track client engagement metrics and identify clients who need attention.
            </p>
            <Badge variant="secondary">Coming Soon</Badge>
          </Card>
        )}
      </div>
    </FeatureGate>
  );
};

export default EngagementHealth;
